// PrivacyPolicy.js
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  FaArrowLeft, 
  FaShieldAlt, 
  FaCookie, 
  FaUserShield, 
  FaDatabase, 
  FaGlobe, 
  FaEnvelope, 
  FaFileContract,
  FaLock,
  FaUsers,
  FaChild,
  FaExchangeAlt
} from "react-icons/fa";
import "./PrivacyPolicy.css";

function PrivacyPolicy() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleBack = () => {
    navigate(-1);
  };

  // const effectiveDate = "July 15, 2026";

  return (
    <div className="privacy-policy-container">
      {/* Header Section */}
      <div className="privacy-header">
        <div className="privacy-header-content">
          <button className="back-button" onClick={handleBack}>
            <FaArrowLeft /> Back
          </button>
          <div className="privacy-header-icon">
            <FaShieldAlt />
          </div>
          <h1>Privacy Policy</h1>
          <p className="privacy-subtitle">
            Your privacy matters to us. Learn how we collect, use, and protect your information.
          </p>
          {/* <div className="effective-date">
            <FaFileContract />
            <span>Effective Date: {effectiveDate}</span>
          </div> */}
        </div>
      </div>

      {/* Main Content */}
      <div className="privacy-content">
        {/* 1. Introduction */}
        <div className="privacy-section">
          <h2>
            <FaShieldAlt className="section-icon" />
            1. Introduction
          </h2>
          <p>
            Welcome to Alvanium Technologies. We respect your privacy 
            and are committed to protecting your personal information.
          </p>
          <p>
            This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
            when you visit our website (https://alvanium.com), use our software products, or interact 
            with our services.
          </p>
          <p className="policy-notice">
            By using our website or services, you agree to the practices described in this Privacy Policy.
          </p>
        </div>

        {/* 2. Information We Collect */}
        <div className="privacy-section">
          <h2>
            <FaDatabase className="section-icon" />
            2. Information We Collect
          </h2>
          <p>We may collect the following types of information:</p>
          
          <h3 className="subsection-title">Personal Information</h3>
          <p>When you voluntarily provide it, we may collect:</p>
          <ul>
            <li>Full name</li>
            <li>Email address</li>
            <li>Company name</li>
            <li>Contact details</li>
            <li>Information submitted through contact forms</li>
            <li>Support requests</li>
            <li>Account information (where applicable)</li>
          </ul>

          <h3 className="subsection-title">Technical Information</h3>
          <p>We may automatically collect:</p>
          <ul>
            <li>IP address</li>
            <li>Browser type</li>
            <li>Device information</li>
            <li>Operating system</li>
            <li>Pages visited</li>
            <li>Referral URLs</li>
            <li>Date and time of access</li>
            <li>Cookies and similar technologies</li>
          </ul>
        </div>

        {/* 3. How We Use Your Information */}
        <div className="privacy-section">
          <h2>
            <FaUserShield className="section-icon" />
            3. How We Use Your Information
          </h2>
          <p>We use your information to:</p>
          <ul>
            <li>Provide and maintain our services</li>
            <li>Respond to inquiries and support requests</li>
            <li>Improve our website and products</li>
            <li>Process purchases and subscriptions</li>
            <li>Send service-related communications</li>
            <li>Prevent fraud and unauthorized access</li>
            <li>Comply with legal obligations</li>
            <li>Monitor website performance and security</li>
          </ul>
        </div>

        {/* 4. Cookies */}
        <div className="privacy-section">
          <h2>
            <FaCookie className="section-icon" />
            4. Cookies
          </h2>
          <p>Our website may use cookies and similar technologies to:</p>
          <ul>
            <li>Remember user preferences</li>
            <li>Improve website performance</li>
            <li>Analyze website traffic</li>
            <li>Enhance user experience</li>
          </ul>
          <p>
            You may disable cookies through your browser settings; however, some website features 
            may not function properly.
          </p>
        </div>

        {/* 5. Third-Party Services */}
        <div className="privacy-section">
          <h2>
            <FaGlobe className="section-icon" />
            5. Third-Party Services
          </h2>
          <p>
            We may use trusted third-party services to operate our business, including but not limited to:
          </p>
          <ul>
            <li><strong>AWS</strong> (hosting and cloud infrastructure)</li>
            <li><strong>Lemon Squeezy</strong> (payment processing and subscription management)</li>
            <li>Analytics providers</li>
            <li>Email service providers</li>
            <li>Customer support platforms</li>
          </ul>
          <p>
            These providers may process personal information only as necessary to provide their services.
          </p>
        </div>

        {/* 6. Payment Information */}
        <div className="privacy-section">
          <h2>
            <FaLock className="section-icon" />
            6. Payment Information
          </h2>
          <p>
            Payments made through our website or software are processed securely by our payment provider, 
            <strong> Lemon Squeezy</strong>.
          </p>
          <p>
            We do not store your payment card information on our servers.
          </p>
          <p>
            Please refer to your payment provider's privacy practices for additional information.
          </p>
        </div>

        {/* 7. Data Security */}
        <div className="privacy-section">
          <h2>
            <FaShieldAlt className="section-icon" />
            7. Data Security
          </h2>
          <p>
            We implement reasonable administrative, technical, and organizational measures to protect 
            your information from unauthorized access, disclosure, alteration, or destruction.
          </p>
          <p>
            While we strive to protect your data, no method of transmission over the Internet or 
            electronic storage is completely secure.
          </p>
        </div>

        {/* 8. Data Retention */}
        <div className="privacy-section">
          <h2>
            <FaDatabase className="section-icon" />
            8. Data Retention
          </h2>
          <p>We retain personal information only for as long as necessary to:</p>
          <ul>
            <li>Provide our services</li>
            <li>Comply with legal obligations</li>
            <li>Resolve disputes</li>
            <li>Enforce our agreements</li>
          </ul>
          <p>When no longer required, information is securely deleted or anonymized.</p>
        </div>

        {/* 9. Sharing of Information */}
        <div className="privacy-section">
          <h2>
            <FaUsers className="section-icon" />
            9. Sharing of Information
          </h2>
          <p>We do not sell your personal information.</p>
          <p>We may share information with:</p>
          <ul>
            <li>Service providers</li>
            <li>Payment processors</li>
            <li>Cloud hosting providers</li>
            <li>Legal authorities when required by law</li>
            <li>Business partners assisting with service delivery</li>
          </ul>
        </div>

        {/* 10. Your Rights */}
        <div className="privacy-section">
          <h2>
            <FaUserShield className="section-icon" />
            10. Your Rights
          </h2>
          <p>Depending on your location and applicable laws, you may have the right to:</p>
          <ul>
            <li>Access your personal information</li>
            <li>Correct inaccurate information</li>
            <li>Request deletion of your information</li>
            <li>Object to certain processing activities</li>
            <li>Withdraw consent where applicable</li>
          </ul>
          <p>
            Requests may be submitted to: <strong>info@alvanium.com</strong>
          </p>
        </div>

        {/* 11. Children's Privacy */}
        <div className="privacy-section">
          <h2>
            <FaChild className="section-icon" />
            11. Children's Privacy
          </h2>
          <p>Our services are not intended for individuals under the age of 13.</p>
          <p>We do not knowingly collect personal information from children.</p>
        </div>

        {/* 12. International Data Transfers */}
        <div className="privacy-section">
          <h2>
            <FaExchangeAlt className="section-icon" />
            12. International Data Transfers
          </h2>
          <p>
            Your information may be processed or stored in countries other than your own where our 
            service providers operate.
          </p>
          <p>We take reasonable measures to protect your information during such transfers.</p>
        </div>

        {/* 13. Changes to this Privacy Policy */}
        <div className="privacy-section">
          <h2>
            <FaFileContract className="section-icon" />
            13. Changes to this Privacy Policy
          </h2>
          <p>We may update this Privacy Policy from time to time.</p>
          <p>Changes become effective when posted on this page.</p>
          <p>We encourage users to review this policy periodically.</p>
        </div>

        {/* 14. Contact Us */}
        <div className="privacy-section">
          <h2>
            <FaEnvelope className="section-icon" />
            14. Contact Us
          </h2>
          <p>If you have any questions regarding this Privacy Policy, please contact us:</p>
          <div className="contact-info-box">
            <p>
              <strong>Alvanium Technologies</strong><br />
              Website: <a href="https://alvanium.com" target="_blank" rel="noopener noreferrer">https://alvanium.com</a><br />
              Email: <a href="mailto:info@alvanium.com">info@alvanium.com</a>
            </p>
          </div>
          <p className="company-tagline">
            <em>Alvanium – Intelligence Behind Every Workflow</em>
          </p>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;